import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FileText,
  Users,
  Clock,
  Copy,
  ArrowLeft,
  Bell,
  XCircle,
  Shield,
  Download,
  ExternalLink,
  CheckCircle2,
  RotateCcw,
  Send,
  MessageSquare,
  History,
  Award,
} from "lucide-react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import PdfCanvas from "@/components/PdfCanvas";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function SignerRow({ r, docId, onRemind }) {
  const link = `${window.location.origin}/sign/${r.access_token}`;
  return (
    <div
      className="flex items-start justify-between gap-3 py-4 border-b border-slate-100 last:border-0"
      data-testid={`doc-recipient-${r.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-slate-900">{r.name}</div>
          <StatusBadge status={r.status} />
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{r.email}</div>
        <div className="text-[11px] font-mono text-slate-400 mt-1 truncate">
          Order {r.order} · role {r.role}
          {r.verification_method && r.verification_method !== "none" && (
            <span className="ml-2 text-emerald-700">
              · verify: {r.verification_method}
            </span>
          )}
        </div>
        {r.verification_method === "otp" && r.otp_code && !r.verified && (
          <div
            className="mt-1.5 inline-flex items-center gap-2 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-mono"
            data-testid={`otp-code-${r.id}`}
          >
            OTP: <span className="font-bold tracking-widest">{r.otp_code}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(r.otp_code);
                toast.success("OTP copied — share securely with the signer");
              }}
              className="text-amber-700 hover:text-amber-900"
              title="Copy"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="text-[11px] font-mono text-slate-400 truncate">
          {link}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(link);
              toast.success("Link copied");
            }}
            className="h-7 text-xs"
            data-testid={`copy-link-${r.id}`}
          >
            <Copy className="h-3 w-3 mr-1" /> Copy link
          </Button>
          <Link to={`/sign/${r.access_token}`} target="_blank">
            <Button variant="outline" size="sm" className="h-7 text-xs">
              <ExternalLink className="h-3 w-3 mr-1" /> Open
            </Button>
          </Link>
          {r.status !== "signed" && r.status !== "declined" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRemind(r.id)}
              className="h-7 text-xs"
              data-testid={`remind-${r.id}`}
            >
              <Bell className="h-3 w-3 mr-1" /> Remind
            </Button>
          )}
        </div>
      </div>
      <div className="text-right text-[11px] font-mono text-slate-400">
        {r.signed_at && <div>signed {new Date(r.signed_at).toLocaleString()}</div>}
        {r.viewed_at && !r.signed_at && (
          <div>viewed {new Date(r.viewed_at).toLocaleString()}</div>
        )}
      </div>
    </div>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
      const [c, v] = await Promise.all([
        api.get(`/documents/${id}/comments`).catch(() => ({ data: [] })),
        api.get(`/documents/${id}/versions`).catch(() => ({ data: [] })),
      ]);
      setComments(c.data);
      setVersions(v.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/app/documents");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const remind = async (rid) => {
    try {
      await api.post(`/documents/${id}/remind/${rid}`);
      toast.success("Reminder recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel this document? It can no longer be signed.")) return;
    try {
      await api.post(`/documents/${id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resend = async () => {
    try {
      await api.post(`/documents/${id}/resend`);
      toast.success("Resend recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/duplicate`);
      toast.success("Duplicated");
      navigate(`/app/documents/${data.id}/prepare`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const approve = async () => {
    try {
      await api.post(`/documents/${id}/approve`, {});
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    try {
      await api.post(`/documents/${id}/reject`, { reason });
      toast.success("Rejected");
      setRejectOpen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendBack = async () => {
    if (!sendBackNote.trim()) return toast.error("Note is required");
    try {
      await api.post(`/documents/${id}/send-back`, { note: sendBackNote });
      toast.success("Sent back to owner");
      setSendBackOpen(false);
      setSendBackNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/documents/${id}/comments`, {
        text: newComment,
        visible_to: "internal",
      });
      setNewComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.title.replace(/\s+/g, "_")}_package.json`;
    a.click();
  };

  if (!doc) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  const canSend = doc.status === "draft" || doc.status === "prepared";
  const myEmail = (user?.email || "").toLowerCase();
  const myApproverRow = doc.recipients.find(
    (r) => r.role === "approver" && r.email?.toLowerCase() === myEmail,
  );
  const canApprove = myApproverRow && !["approved", "rejected"].includes(myApproverRow.status);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="doc-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 mb-2 -ml-2 text-slate-600"
            onClick={() => navigate("/app/documents")}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-slate-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight break-words">
                {doc.title}
              </h1>
              <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="truncate max-w-[180px] sm:max-w-none">{doc.file_name}</span>
                <span className="font-mono">#{doc.id.slice(0, 8)}</span>
                <StatusBadge status={doc.status} />
              </div>
            </div>
          </div>
        </div>
        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto">
          <div className="flex items-center gap-2 w-max sm:w-auto sm:flex-wrap pb-2 sm:pb-0">
          {canApprove && (
            <>
              <Button
                onClick={approve}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                data-testid="approve-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectOpen(true)}
                className="text-red-600 border-red-200 shrink-0"
                data-testid="reject-btn"
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => setSendBackOpen(true)}
                className="shrink-0"
                data-testid="send-back-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Send back
              </Button>
            </>
          )}
          {canSend && (
            <Button
              onClick={() => navigate(`/app/documents/${id}/prepare`)}
              className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
              data-testid="doc-edit-btn"
            >
              Prepare & send
            </Button>
          )}
          {["sent", "viewed", "partially_signed"].includes(doc.status) && (
            <>
              <Button variant="outline" onClick={resend} className="shrink-0" data-testid="doc-resend-btn">
                <Send className="h-4 w-4 mr-1" /> Resend
              </Button>
              <Button variant="outline" onClick={cancel} className="shrink-0" data-testid="doc-cancel-btn">
                <XCircle className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </>
          )}
          <Button variant="outline" onClick={duplicate} className="shrink-0" data-testid="doc-duplicate-btn">
            <Copy className="h-4 w-4 mr-1" /> Duplicate
          </Button>
          {doc.status === "completed" && (
            <>
              <Link to={`/app/documents/${id}/certificate`} className="shrink-0">
                <Button variant="outline" data-testid="doc-certificate-btn">
                  <Award className="h-4 w-4 mr-1" /> Certificate
                </Button>
              </Link>
              <a
                href={`${API_BASE}/documents/${id}/download/signed.pdf`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
              >
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="doc-download-pdf"
                >
                  <Download className="h-4 w-4 mr-1" /> Signed PDF
                </Button>
              </a>
              <a
                href={`${API_BASE}/documents/${id}/download/certificate.pdf`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
              >
                <Button variant="outline" data-testid="doc-download-cert-pdf">
                  <Download className="h-4 w-4 mr-1" /> Cert PDF
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={downloadJson}
                className="shrink-0"
                data-testid="doc-download-package"
              >
                <Download className="h-4 w-4 mr-1" /> JSON
              </Button>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Meta stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Recipients</div>
          <div className="text-lg sm:text-xl font-semibold mt-1 flex items-center gap-1.5">
            <Users className="h-4 w-4 text-slate-500" />
            {doc.recipients.length}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Fields</div>
          <div className="text-lg sm:text-xl font-semibold mt-1">{doc.fields.length}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Mode</div>
          <div className="text-lg sm:text-xl font-semibold mt-1 capitalize">{doc.signing_mode}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Created</div>
          <div className="text-xs sm:text-sm font-mono mt-1.5 sm:mt-2 text-slate-700">
            {new Date(doc.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      <Tabs defaultValue="recipients" className="mt-8">
        <div className="-mx-4 sm:mx-0 overflow-x-auto px-4 sm:px-0">
          <TabsList className="w-max">
            <TabsTrigger value="recipients" data-testid="tab-recipients">
              Recipients
            </TabsTrigger>
            <TabsTrigger value="document" data-testid="tab-document">
              Document
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              Audit
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">
              Notes ({comments.length})
            </TabsTrigger>
            <TabsTrigger value="versions" data-testid="tab-versions">
              Versions ({versions.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="recipients">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            {doc.recipients.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-6">
                No recipients added yet.
              </div>
            )}
            {doc.recipients.map((r) => (
              <SignerRow key={r.id} r={r} docId={doc.id} onRemind={remind} />
            ))}
            {doc.message && (
              <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
                  Message
                </div>
                {doc.message}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="document">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            {doc.file_data_url ? (
              <PdfCanvas
                fileUrl={doc.file_data_url}
                pageNumber={page}
                onLoadPages={(n) => setPage((p) => Math.min(p, n))}
                renderOverlay={() =>
                  doc.fields
                    .filter((f) => f.page === page)
                    .map((f) => (
                      <div
                        key={f.id}
                        className="absolute border-2 border-emerald-500 bg-emerald-50/40 rounded-sm flex items-center justify-center text-[10px] font-medium text-emerald-700"
                        style={{
                          left: f.x,
                          top: f.y,
                          width: f.width,
                          height: f.height,
                        }}
                      >
                        {f.value ? (
                          f.type === "signature" || f.type === "initials" ? (
                            <img src={f.value} alt="" className="max-h-full max-w-full" />
                          ) : (
                            <span className="text-slate-900 text-xs">{f.value}</span>
                          )
                        ) : (
                          f.type
                        )}
                      </div>
                    ))
                }
              />
            ) : (
              <div className="text-sm text-slate-500">Document file not loaded.</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-5">
              <Shield className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-sm font-semibold">Tamper-evident audit trail</div>
                <div className="text-xs text-slate-500">
                  SHA-256 · <span className="font-mono">{doc.hash}</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
              {doc.audit.map((ev) => (
                <div key={ev.id} className="relative pl-8 pb-5" data-testid={`audit-${ev.event}`}>
                  <div className="absolute left-0 top-1 h-4 w-4 rounded-full bg-white border-2 border-emerald-500" />
                  <div className="text-sm font-medium text-slate-900">
                    {ev.event}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-mono flex items-center gap-3 flex-wrap">
                    <span>
                      <Clock className="inline h-3 w-3 mr-1" />
                      {new Date(ev.at).toLocaleString()}
                    </span>
                    {ev.actor_email && <span>{ev.actor_email}</span>}
                    {ev.ip && <span>{ev.ip}</span>}
                  </div>
                  {Object.keys(ev.meta || {}).length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 font-mono">
                      {JSON.stringify(ev.meta)}
                    </div>
                  )}
                </div>
              ))}
              {doc.status === "completed" && (
                <div className="relative pl-8 mt-3 rounded-md bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-sm font-semibold text-emerald-800">
                    Certificate of completion generated
                  </div>
                  <div className="text-xs text-emerald-700 mt-1">
                    This document was completed on{" "}
                    <span className="font-mono">
                      {doc.completed_at ? new Date(doc.completed_at).toLocaleString() : ""}
                    </span>
                    . All recipients signed.
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notes">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="notes-panel">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold">Internal notes</h3>
              <span className="text-xs text-slate-500">— visible only to your team</span>
            </div>

            <div className="mt-4 space-y-3">
              {comments.length === 0 && (
                <div className="text-xs text-slate-500 py-4">
                  No notes yet. Use this space for internal context.
                </div>
              )}
              {comments.map((c) => (
                <div
                  key={c.id}
                  className="rounded-lg border border-slate-200 dark:border-slate-800 p-3"
                  data-testid={`note-${c.id}`}
                >
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {c.author_name}
                    </span>
                    <span>·</span>
                    <span className="font-mono">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1.5 text-sm whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <Input
                placeholder="Add a note for your team…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                data-testid="note-input"
              />
              <Button
                onClick={addComment}
                disabled={!newComment.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="note-submit"
              >
                Post
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="versions">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold">Version history</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between rounded-md bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2">
                <div>
                  <div className="font-semibold">Version {doc.version_number || 1} (current)</div>
                  <div className="text-xs text-slate-500 capitalize">{doc.status.replace(/_/g, " ")}</div>
                </div>
              </div>
              {versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2">
                  <div>
                    <div className="font-medium">Version {v.version_number}</div>
                    <div className="text-xs text-slate-500 capitalize">
                      Snapshot · {v.status_at_snapshot}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-slate-500">
                    {new Date(v.snapshot_at).toLocaleString()}
                  </div>
                </div>
              ))}
              {versions.length === 0 && (
                <div className="text-xs text-slate-500">
                  No older versions yet. New versions are created when an approver sends the document back.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Capture a reason. The document owner will be notified.
          </p>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection…"
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={reject}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send back for changes</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Document will move to draft. Recipients are reset and version is bumped.
          </p>
          <Textarea
            value={sendBackNote}
            onChange={(e) => setSendBackNote(e.target.value)}
            placeholder="What needs to change?"
            rows={4}
            data-testid="send-back-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendBackOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendBack}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="send-back-confirm"
            >
              Send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
